import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  ArrowUpRight,
  AlertCircle,
  Filter,
} from "lucide-react";

import { EMPTY_SPYSERP_FILTER_OPTIONS, EMPTY_SPYSERP_GLOBAL_ALERTS } from "@/types/spyserp";
import { type DateRangeOption } from "@/components/clicks/DateRangeFilter";
import { CopyIconButton } from "@/components/clicks/CopyableValue";
import { parseUrlActiveFilters } from "@/lib/url-active-filters";
import { useSpyserpFilterOptions } from "@/hooks/useSpyserpFilterOptions";
import { useSpyserpDomainStats } from "@/hooks/useSpyserpDomainStats";
import { useSpyserpGlobalAlerts } from "@/hooks/useSpyserpGlobalAlerts";
import { useSpyserpDateRange } from "@/hooks/useSpyserpDateRange";
import {
  toSpyserpSegmentFilterOptions,
  useSpyserpSegments,
} from "@/hooks/useSpyserpSegments";
import { SortableHead } from "./SortableHead";
import { SiteTypeBadge } from "./SiteTypeBadge";
import { DomainKeywordsModal } from "./DomainKeywordsModal";
import { SpySerpFiltersSidebar } from "./SpySerpFiltersSidebar";

export default function DomainsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);

  const pageSize = parseInt(searchParams.get("limit") || "15", 10);
  const validDomainCols = [
    "domain",
    "total_kw",
    "top_3",
    "top_10",
    "top_20",
    "current_avg_rank",
    "rank_change",
  ];
  const rawSortCol = searchParams.get("sort") || "total_kw";
  const sortCol = validDomainCols.includes(rawSortCol)
    ? rawSortCol
    : "total_kw";
  const sortDesc = searchParams.get("desc") !== "false";

  const filtersParam = searchParams.get("filters");
  const activeFilters = parseUrlActiveFilters(filtersParam);

  const dateRange = (searchParams.get("dateRange") ||
    "last_30_days") as DateRangeOption;
  const customFrom = searchParams.get("from") || undefined;
  const customTo = searchParams.get("to") || undefined;

  const [selectedDomainData, setSelectedDomainData] = useState<{
    domain: string;
    rankLimit?: number;
  } | null>(null);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const dateRangeParams = useMemo(
    () => ({ dateRange, customFrom, customTo }),
    [dateRange, customFrom, customTo],
  );

  const { data: filterOptions = EMPTY_SPYSERP_FILTER_OPTIONS } =
    useSpyserpFilterOptions();
  const { data: resolvedDates } = useSpyserpDateRange(dateRangeParams);
  const { data: segmentRows = [] } = useSpyserpSegments({
    enabled: isFiltersOpen,
  });
  const segmentFilterOptions = useMemo(
    () => toSpyserpSegmentFilterOptions(segmentRows),
    [segmentRows],
  );

  const { data: domainData, isPending: domainStatsPending } =
    useSpyserpDomainStats({
      page,
      pageSize,
      sortCol,
      sortDesc,
      filtersParam,
      activeFilters,
      ...dateRangeParams,
    });

  const { data: alerts = EMPTY_SPYSERP_GLOBAL_ALERTS } =
    useSpyserpGlobalAlerts(dateRangeParams);

  const stats = domainData?.stats ?? [];
  const totalCount = domainData?.totalCount ?? 0;
  const loading = domainStatsPending;

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set("page", newPage.toString());
      return prev;
    });
  };

  const handleLimitChange = (newLimit: string) => {
    setSearchParams((prev) => {
      prev.set("limit", newLimit);
      prev.set("page", "1");
      return prev;
    });
  };

  const handleSort = (column: string) => {
    setSearchParams((prev) => {
      if (sortCol === column) {
        prev.set("desc", (!sortDesc).toString());
      } else {
        prev.set("sort", column);
        prev.set("desc", "true");
      }
      prev.set("page", "1");
      return prev;
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const renderTrend = (change: number) => {
    if (!change || change === 0) {
      return (
        <span className="flex items-center justify-end text-sm text-muted-foreground">
          <Minus className="mr-1 h-3 w-3" /> —
        </span>
      );
    }
    const trendClass =
      change > 0
        ? "signal-text-positive flex items-center justify-end text-sm font-medium tabular-nums"
        : "signal-text-negative flex items-center justify-end text-sm font-medium tabular-nums";
    const Icon = change > 0 ? TrendingUp : TrendingDown;
    return (
      <span className={trendClass}>
        <Icon className="mr-1 h-3 w-3" /> {change > 0 ? `+${change}` : change}
      </span>
    );
  };

  return (
    <div className="flex h-full w-full">
      <aside
        className={`flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
          isFiltersOpen ? "w-64 opacity-100 mr-6" : "w-0 opacity-0"
        }`}
      >
        <SpySerpFiltersSidebar
          availableOptions={filterOptions}
          segments={segmentFilterOptions}
        />
      </aside>

      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        <DomainKeywordsModal
          isOpen={!!selectedDomainData}
          onClose={() => setSelectedDomainData(null)}
          domain={selectedDomainData?.domain || null}
          rankLimit={selectedDomainData?.rankLimit || undefined}
          startDate={resolvedDates?.startDate}
          endDate={resolvedDates?.endDate}
        />

        <div className="space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            🚨 Signals & Alerts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="signal-card-negative border">
              <CardContent className="flex h-full flex-col justify-between p-4">
                <div className="flex items-start justify-between">
                  <AlertTriangle className="signal-text-negative mb-2 h-5 w-5" />
                  <span className="text-2xl font-bold tabular-nums signal-text-negative">
                    {alerts.dropped_top_50}
                  </span>
                </div>
                <div>
                  <p className="font-semibold signal-text-negative">
                    Dropped from Top 50
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Keywords that fell out of top 50 positions
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="signal-card-positive border">
              <CardContent className="flex h-full flex-col justify-between p-4">
                <div className="flex items-start justify-between">
                  <ArrowUpRight className="signal-text-positive mb-2 h-5 w-5" />
                  <span className="text-2xl font-bold tabular-nums signal-text-positive">
                    {alerts.entered_top_10}
                  </span>
                </div>
                <div>
                  <p className="font-semibold signal-text-positive">
                    Entered Top 10
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Keywords that moved into top 10
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="signal-card-warning border">
              <CardContent className="flex h-full flex-col justify-between p-4">
                <div className="flex items-start justify-between">
                  <AlertCircle className="signal-text-warning mb-2 h-5 w-5" />
                  <span className="text-2xl font-bold tabular-nums signal-text-warning">
                    {alerts.dropped_top_10}
                  </span>
                </div>
                <div>
                  <p className="font-semibold signal-text-warning">
                    Dropped from Top 10
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Keywords fell out of tracking range
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold">
                Domain Performance Overview
              </h3>
            </div>

            <Button
              variant={isFiltersOpen ? "secondary" : "outline"}
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              className="gap-2 cursor-pointer"
            >
              <Filter className="w-4 h-4" />
              Filters
              {Object.keys(activeFilters).length > 0 && (
                <Badge variant="default" className="ml-1 px-1.5 h-5">
                  {Object.keys(activeFilters).length}
                </Badge>
              )}
            </Button>
          </div>

          <div className="border rounded-md bg-card flex flex-col">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <SortableHead
                    label="Domain"
                    column="domain"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Keywords"
                    column="total_kw"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Top 3"
                    column="top_3"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Top 10"
                    column="top_10"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Top 20"
                    column="top_20"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Avg Pos"
                    column="current_avg_rank"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="Change"
                    column="rank_change"
                    align="right"
                    currentSort={sortCol}
                    isSortDesc={sortDesc}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-muted-foreground"
                    >
                      Loading domains data...
                    </TableCell>
                  </TableRow>
                ) : stats.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No data available for the selected period.
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.map((row) => (
                    <TableRow key={row.domain}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <span
                            className="cursor-pointer hover:text-primary hover:underline"
                            onClick={() =>
                              setSelectedDomainData({ domain: row.domain })
                            }
                          >
                            {row.domain}
                          </span>
                          <Link to={`https://${row.domain}`} target="_blank">
                            <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-primary" />
                          </Link>
                          <CopyIconButton value={row.domain} />
                        </div>
                        <SiteTypeBadge
                          siteType={row.site_type}
                          className="mt-1"
                        />
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {row.total_kw}
                      </TableCell>
                      <TableCell
                        className="text-right cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() =>
                          setSelectedDomainData({
                            domain: row.domain,
                            rankLimit: 3,
                          })
                        }
                      >
                        <span className="font-medium tabular-nums signal-text-warning">
                          {row.top_3}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({row.top_3_percent || 0}%)
                        </span>
                      </TableCell>
                      <TableCell
                        className="text-right cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() =>
                          setSelectedDomainData({
                            domain: row.domain,
                            rankLimit: 10,
                          })
                        }
                      >
                        <span className="font-medium tabular-nums signal-text-positive">
                          {row.top_10}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({row.top_10_percent || 0}%)
                        </span>
                      </TableCell>
                      <TableCell
                        className="text-right cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() =>
                          setSelectedDomainData({
                            domain: row.domain,
                            rankLimit: 20,
                          })
                        }
                      >
                        <span className="font-medium tabular-nums text-[var(--chart-2-fg)]">
                          {row.top_20}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({row.top_20_percent || 0}%)
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className="font-mono bg-muted/30"
                        >
                          #{row.current_avg_rank}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right pt-4">
                        {renderTrend(row.rank_change)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-6 px-1">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Showing</span>
              <Select
                value={pageSize.toString()}
                onValueChange={handleLimitChange}
              >
                <SelectTrigger className="w-[80px] h-9">
                  <SelectValue placeholder="15" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">domains</span>
            </div>

            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
